import {
  SitecoreContext,
  ComponentPropsContext,
  LayoutServiceData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { GetServerSideProps } from 'next';
import { useEffect } from 'react';
import PdfTemplate from 'src/templates/PdfTemplate';
// different componentFactory method will be used based on whether page is being edited
import { componentFactory } from 'temp/componentFactory';

import { EngineProvider } from 'src/hooks/useCoveoEngine';
import { GlobalStoreProvider } from 'src/hooks/useGlobalStore';
import { PdfAdditionalData, PdfStoreProvider } from 'src/hooks/usePdfStore';

import { parsePdfOptionsParam } from 'src/utils/pdfOptionsParam';

import { logger } from 'lib/logger';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';

type SitecorePagePropsWithAdditionalData = SitecorePageProps & {
  additionalData?: PdfAdditionalData;
};

const SitecorePage = ({
  componentProps,
  layoutData,
  additionalData,
}: SitecorePagePropsWithAdditionalData): JSX.Element => {
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  return (
    <GlobalStoreProvider>
      <PdfStoreProvider additionalData={additionalData}>
        <EngineProvider>
          <ComponentPropsContext value={componentProps}>
            <SitecoreContext componentFactory={componentFactory} layoutData={layoutData}>
              <PdfTemplate layoutData={layoutData} />
            </SitecoreContext>
          </ComponentPropsContext>
        </EngineProvider>
      </PdfStoreProvider>
    </GlobalStoreProvider>
  );
};

type LayoutServiceDataWithRedirectUrl = LayoutServiceData & {
  sitecore: {
    context: {
      redirectUrl?: string;
    };
  };
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const parsedContext = structuredClone({
    params: context.params,
    locales: context.locales,
    locale: context.locale,
    defaultLocale: context.defaultLocale,
  });

  if (parsedContext.params && Array.isArray(parsedContext.params?.path)) {
    const site = Array.isArray(parsedContext.params.site)
      ? parsedContext.params.site[0]
      : parsedContext.params.site || '';
    if (site) {
      parsedContext.params.path.unshift(site);
    }
  }

  const props = await sitecorePagePropsFactory.create(parsedContext);

  const customLayoutData = props.layoutData as LayoutServiceDataWithRedirectUrl;

  // Check if we have a redirect via the redirects module. Note: redirectUrl is not populated when FETCH_WITH=GraphQL
  if (customLayoutData?.sitecore?.context?.redirectUrl) {
    return {
      redirect: {
        destination: customLayoutData.sitecore.context.redirectUrl,
        permanent: true,
      },
    };
  }

  // Check if we have a redirect (e.g. custom error page)
  if (props?.redirect) {
    return {
      redirect: props.redirect,
    };
  }

  const additionalData: PdfAdditionalData = {};

  // Check if we are rendering the Professional/People TC Detail PDF page
  // If so, fetch the full list of related publications, news, and events
  const pdfOptions = parsePdfOptionsParam(context.query);
  if (isPeopleDetailPage(props) && pdfOptions.fullBio) {
    additionalData.professionalRelatedInsights = await fetchProfessionalRelatedInsights(props);
  }

  return {
    props: {
      ...props,
      additionalData,
    },
    notFound: props.notFound, // Returns custom 404 page with a status code of 404 when true
  };

  function isPeopleDetailPage(props: SitecorePageProps) {
    return ['Professional', 'Person TC'].includes(
      props.layoutData.sitecore.route?.templateName || ''
    );
  }

  async function fetchProfessionalRelatedInsights(props: SitecorePageProps) {
    const sitecoreContext = props.layoutData.sitecore;
    const id = sitecoreContext.route?.itemId || '';
    const { language = '', site } = sitecoreContext.context;

    try {
      const endpoint = new URL('/sitecore/api/insights/peoplepdf', process.env.SITECORE_API_HOST);
      endpoint.searchParams.set('id', id);
      endpoint.searchParams.set('sc_lang', language);
      endpoint.searchParams.set('sc_site', site?.name || '');
      const response = await fetch(endpoint);
      const json = await response.json();
      return json;
    } catch (error) {
      logger.error(error);
      return;
    }
  }
};

export default SitecorePage;
